// src/pages/financials/ConnectionTab.tsx
//
// Connection — QBD module status (/api/qbd/status) + manual "Sync Now"
// (/api/qbd/sync). A 404 from the status endpoint means the QuickBooks
// Desktop module isn't enabled on this tenant's agent — render a friendly
// panel instead of an error box. Timestamps from the agent lack a 'Z'
// suffix, so lastRun.ranAt goes through fmtLocalTime.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plug, RefreshCw, AlertTriangle, PlugZap } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import { fmtLocalTime } from '../../lib/formatTime';

const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(v);

function is404(err: unknown): boolean {
  return !!err && typeof err === 'object'
    && (err as { response?: { status?: number } }).response?.status === 404;
}

export function ConnectionTab() {
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['qbd', 'status'],
    queryFn: () => api.getQbdStatus(),
    staleTime: 30_000,
    retry: (failureCount, error) => !is404(error) && failureCount < 1,
  });

  const syncMutation = useMutation({
    mutationFn: () => api.runQbdSync(),
    onSettled: () => {
      // Refresh the status card AND every qbd-namespaced data query
      // (P&L, aging, expense breakdown) with the newly synced rows.
      queryClient.invalidateQueries({ queryKey: ['qbd'] });
    },
  });

  const status = q.data;
  const lastRun = status?.lastRun ?? null;

  // Module not enabled on this tenant's agent — friendly panel, not an error.
  if (is404(q.error)) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-slate-800/60 border border-slate-700/40 flex items-center justify-center mx-auto">
          <Plug size={24} className="text-slate-500" />
        </div>
        <h2 className="text-xl font-semibold text-white">QuickBooks Desktop is not enabled</h2>
        <p className="text-sm text-slate-400">
          The QuickBooks Desktop module is not enabled on this tenant's agent.
          Connection mode and credentials are configured per tenant on the agent
          (appsettings QuickBooksDesktop section); the feature is toggled in System Admin → Features.
        </p>
      </div>
    );
  }

  const statusPill = status
    ? status.enabled
      ? { label: status.mode === 'mock' ? 'Connected (mock)' : 'Connected (connector)', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' }
      : { label: 'Not enabled', cls: 'bg-slate-700/40 text-slate-400 border-slate-600/40' }
    : null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Plug size={20} /> Connection
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          QuickBooks Desktop sync status and manual refresh.
        </p>
      </div>

      {q.error && !is404(q.error) && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the connection status</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : status && (
        <div className="max-w-3xl space-y-4">
          {/* Status card */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                {statusPill && (
                  <span className={`text-xs px-2 py-1 rounded-md border font-medium ${statusPill.cls}`}>{statusPill.label}</span>
                )}
                {q.isFetching && <RefreshCw size={12} className="text-slate-500 animate-spin" />}
              </div>
              <button
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending || !status.enabled}
                className="px-3 py-2 rounded-lg bg-indigo-500/15 border border-indigo-500/40 text-indigo-200 text-sm hover:bg-indigo-500/25 transition-all disabled:opacity-50 flex items-center gap-2">
                {syncMutation.isPending
                  ? <><RefreshCw size={14} className="animate-spin" /> Syncing…</>
                  : <><PlugZap size={14} /> Sync Now</>}
              </button>
            </div>

            {syncMutation.error && (
              <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-xs flex items-center gap-2">
                <AlertTriangle size={14} />
                <span>Sync failed: {(syncMutation.error as Error).message}</span>
              </div>
            )}

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-xs text-slate-500">Company file</dt>
                <dd className="text-slate-200 break-all">{status.companyFile || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Connector URL</dt>
                <dd className="text-slate-200 break-all">{status.connectorUrl || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Sync interval</dt>
                <dd className="text-slate-200">{status.intervalMinutes} minutes</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Last sync</dt>
                <dd className="text-slate-200">
                  {lastRun ? fmtLocalTime(lastRun.ranAt) : 'Never'}
                  {lastRun && <span className="text-slate-500 text-xs ml-1.5">({(lastRun.durationMs / 1000).toFixed(1)}s)</span>}
                </dd>
              </div>
            </dl>

            {lastRun?.error && (
              <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-xs flex items-center gap-2">
                <AlertTriangle size={14} />
                <span>Last sync error: {lastRun.error}</span>
              </div>
            )}
          </div>

          {/* Row counts from the last run */}
          {lastRun && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {([
                ['Accounts',      lastRun.accounts],
                ['GL Lines',      lastRun.txnLines],
                ['Customers',     lastRun.customers],
                ['Vendors',       lastRun.vendors],
                ['Open Invoices', lastRun.openInvoices],
                ['Open Bills',    lastRun.openBills],
                ['Employees',     lastRun.employees],
                ['Time Cards',    lastRun.timeCards],
              ] as const).map(([label, value]) => (
                <div key={label} className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="text-lg font-bold text-white tabular-nums">{fmtNum(value)}</p>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-slate-500">
            Connection mode and credentials are configured per tenant on the agent
            (appsettings QuickBooksDesktop section); the feature is toggled in System Admin → Features.
          </p>
        </div>
      )}
    </div>
  );
}
